import {
	CAR_CHECKIN_ZONE_TOP_OFFSET_PCT,
	CAR_GIFT_UI_EXTRA_TOP_OFFSET_PCT,
	CAR_SEAL_ZONE_DEBUG_BG,
	CAR_GIFT_CHECKIN_ZONE_DEFAULT,
	CAR_GIFT_LEGACY_POINT,
	CAR_GIFT_STATUS_IMAGES
} from '../api/config.js'
import {
	applyStyleToElement,
	getCarvueAdjustedZoneAnchor,
	getCarvueFullWidthSquareZoneStyle,
	getCarvueSlideHeight,
	parseAct004HotspotNumber
} from './act004-hotspot-style.js'
import { cvQuery } from './dom.js'
import { isAct004LinkCheckinZoneHotspot } from '../../../utils/act004-link-checkin-zone.js'

/** 状态图在盖章区内的纵向比例（与原 CSS top:54% 一致） */
const CAR_GIFT_STATUS_IMG_ZONE_Y_RATIO = 0.54

function parsePageConfig(activity) {
	if (!activity) return {}
	let raw = activity.pageConfigJson != null ? activity.pageConfigJson : activity.pageConfig
	if (typeof raw === 'string') {
		try {
			raw = JSON.parse(raw)
		} catch (e) {
			raw = {}
		}
	}
	return raw && typeof raw === 'object' ? raw : {}
}

function findGiftCheckinPage(activity) {
	const pageConfig = parsePageConfig(activity)
	const pages = Array.isArray(pageConfig.pages) ? pageConfig.pages : []
	var i
	for (i = 0; i < pages.length; i++) {
		if (String((pages[i] && pages[i].pageName) || '').trim() === '拉享好礼') {
			return pages[i]
		}
	}
	return pages.length ? pages[0] : null
}

/** 优先活动配置 type=22 联动热区，否则用设计稿默认中心区域 */
export function resolveGiftCheckinZoneAnchor(activity) {
	const pageConfig = parsePageConfig(activity)
	const page = findGiftCheckinPage(activity)
	if (page && Array.isArray(page.hotspots)) {
		for (let i = 0; i < page.hotspots.length; i++) {
			if (isAct004LinkCheckinZoneHotspot(page.hotspots[i])) {
				return page.hotspots[i]
			}
		}
	}
	const pages = Array.isArray(pageConfig.pages) ? pageConfig.pages : []
	for (let p = 0; p < pages.length; p++) {
		const list = pages[p] && Array.isArray(pages[p].hotspots) ? pages[p].hotspots : []
		for (let i = 0; i < list.length; i++) {
			if (isAct004LinkCheckinZoneHotspot(list[i])) {
				return list[i]
			}
		}
	}
	return CAR_GIFT_CHECKIN_ZONE_DEFAULT
}

/** locked=待解锁 unlocked=已解锁未核销 redeemed=已核销 */
export function resolveGiftCheckinState(pointSet) {
	const set = pointSet || new Set()
	if (set.has(CAR_GIFT_LEGACY_POINT)) {
		return 'redeemed'
	}
	const baseDone = ['one', 'three'].every(function (p) {
		return set.has(p)
	})
	if (baseDone) {
		return 'unlocked'
	}
	return 'locked'
}

function swapGiftStatusImage(img, zone, src, state) {
	const applyMeta = function () {
		img.setAttribute('data-gift-state', state)
		if (zone) {
			zone.style.pointerEvents = state === 'unlocked' ? 'auto' : 'none'
		}
	}
	const currentSrc = img.getAttribute('src') || img.src
	if (currentSrc === src) {
		applyMeta()
		return
	}
	const preload = new Image()
	preload.onload = function () {
		img.src = src
		applyMeta()
	}
	preload.onerror = function () {
		img.src = src
		applyMeta()
	}
	preload.src = src
}

/** 仅切换状态图，不触发布局重算，避免核销后画面跳动 */
export function syncGiftCheckinVisual(pointSet) {
	const img = cvQuery('.gift_checkin_status_img')
	const zone = cvQuery('.gift_checkin_verify_zone')
	if (!img) return
	const state = resolveGiftCheckinState(pointSet)
	const src = CAR_GIFT_STATUS_IMAGES[state] || CAR_GIFT_STATUS_IMAGES.locked
	if (img.getAttribute('data-gift-state') === state && (img.getAttribute('src') || img.src) === src) {
		return
	}
	swapGiftStatusImage(img, zone, src, state)
}

/** 状态图固定在 slide 层，与四指盖章区解耦，换图时位置不变 */
export function applyGiftCheckinStatusImgLayout() {
	const img = cvQuery('.gift_checkin_status_img')
	const slide = cvQuery('.carvue-root .swiper-slide.gift_slide')
	if (!img || !slide) return
	const activity = window.__carActivityDetail
	const anchor = resolveGiftCheckinZoneAnchor(activity)
	const giftTopOffset =
		CAR_CHECKIN_ZONE_TOP_OFFSET_PCT + CAR_GIFT_UI_EXTRA_TOP_OFFSET_PCT
	const adjusted = getCarvueAdjustedZoneAnchor(
		anchor,
		CAR_GIFT_CHECKIN_ZONE_DEFAULT.top,
		giftTopOffset
	)
	const slideRect = slide.getBoundingClientRect()
	const slideH = slideRect.height
	const slideW = slideRect.width
	if (!slideH || !slideW) return
	const topPct = parseAct004HotspotNumber(adjusted.top, 50)
	const zoneCenterY = (topPct / 100) * slideH
	const zoneSize = slideW
	const imgCenterY = zoneCenterY + (CAR_GIFT_STATUS_IMG_ZONE_Y_RATIO - 0.5) * zoneSize
	const topValue = (imgCenterY / slideH) * 100 + '%'
	applyStyleToElement(img, {
		position: 'absolute',
		left: '50%',
		top: topValue,
		width: '70%',
		maxWidth: '4.25rem',
		height: 'auto',
		transform: 'translate(-50%, -50%)',
		zIndex: '10',
		pointerEvents: 'none'
	})
}

export function applyGiftCheckinVerifyLayout() {
	const zone = cvQuery('.gift_checkin_verify_zone')
	if (!zone) return
	const activity = window.__carActivityDetail
	const anchor = resolveGiftCheckinZoneAnchor(activity)
	const giftTopOffset =
		CAR_CHECKIN_ZONE_TOP_OFFSET_PCT + CAR_GIFT_UI_EXTRA_TOP_OFFSET_PCT
	const adjusted = getCarvueAdjustedZoneAnchor(
		anchor,
		CAR_GIFT_CHECKIN_ZONE_DEFAULT.top,
		giftTopOffset
	)
	const slideH = getCarvueSlideHeight('.carvue-root .swiper-slide.gift_slide')
	const boxStyle = getCarvueFullWidthSquareZoneStyle(adjusted, slideH, {
		opacity: '1',
		background: CAR_SEAL_ZONE_DEBUG_BG,
		touchAction: 'none',
		pointerEvents: 'auto',
		zIndex: '11'
	})
	applyStyleToElement(zone, boxStyle)

	const canvas = cvQuery('.checkinCanvas2')
	if (canvas) {
		applyStyleToElement(canvas, {
			position: 'absolute',
			left: '0',
			top: '0',
			width: '100%',
			height: '100%',
			maxWidth: 'none',
			aspectRatio: 'unset',
			zIndex: '12',
			opacity: '0',
			touchAction: 'none'
		})
	}

	applyGiftCheckinStatusImgLayout()
}

let resizeHandler = null

export function bindGiftCheckinVerifyResize() {
	if (resizeHandler || typeof window === 'undefined') return
	resizeHandler = function () {
		applyGiftCheckinVerifyLayout()
	}
	window.addEventListener('resize', resizeHandler, { passive: true })
}

export function unbindGiftCheckinVerifyResize() {
	if (resizeHandler && typeof window !== 'undefined') {
		window.removeEventListener('resize', resizeHandler)
	}
	resizeHandler = null
}
